import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { isIncidentDocument } from '../../shared/media';
import { firstValueFrom } from 'rxjs';
import { IncidentService } from '../../shared/incident.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Incident, IncidentCloseRequest, IncidentLocation, IncidentOperator, IncidentTyre, ServiceProviderStatusUpdate, Document, BDTechIncidentStatusUpdate } from '../../models/incident';
import { AppModule } from '../../app.module';
import { NotificationService } from '../../shared/notification.service';
import { IncidentType, TyreLocation, UserType } from '../../shared/enums';
import { MapSelect } from "../../map/map-select/map-select";
import { IncidentTyreComponent } from './incident-tyre/incident-tyre';
import { SharedDataService } from '../../shared/shareddata.service';
import { AuthService } from '../../auth.service';
import { BreakdownTechnician, ClientLink, Driver, FleetClient, IncidentClientLink, SavedLocation, ServiceProviderClient, Transporter, Vehicle } from '../../models/client';
import { ClientService } from '../../shared/client.service';
import { QuoteService } from '../../shared/quote.service';
import { Location } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import { concatMap, filter, finalize, shareReplay, take, tap } from 'rxjs/operators';
import { WhatsappService } from '../../whatsapp/whatsapp.service';
import { WaGroupLinkDirective } from '../../shared/wa-group-link.directive';
import { PriceTemplate } from '../../models/pricing';
import { Globals } from '../../shared/functions';
import { ReportService } from '../../shared/report.service';
import { Base64Report } from '../../models/report';
import { FileUploader } from '../../shared/file-uploader/file-uploader';
import { IncidentDocumentTile } from "../incident-document-tile/incident-document-tile";
import { IncidentCloseModal } from "../incident-close-modal/incident-close-modal";
import { ServiceProviderService } from '../../service-provider/service-provider.service';
import { MapServiceProviders } from '../../map/map-service-providers/map-service-providers';
import { WaGroupOpener } from '../../shared/wa-group-opener/wa-group-opener';
import { ValidationBorderDirective } from '../../shared/validation-border.directive';
import { IncidentChat } from '../../shared/incident-chat/incident-chat';
import { ChatSenderType } from '../../models/chat';


@Component({
  selector: 'app-incident',
  imports: [AppModule, MapSelect, IncidentTyreComponent, FileUploader, IncidentDocumentTile, IncidentCloseModal, MapServiceProviders, WaGroupOpener, WaGroupLinkDirective, ValidationBorderDirective, IncidentChat],
  templateUrl: './incident.html',
  styleUrl: './incident.less'
})
export class IncidentComponent implements OnInit, OnDestroy {
  readonly chatSender = ChatSenderType.Callcentre;
  public chatIncidentId = 0;
  public get visibleDocuments() { return (this.incident?.documents || []).filter(isIncidentDocument); }


  public incident: Incident = new Incident();
  public savedLocation: SavedLocation = new SavedLocation();
  public incidentType = IncidentType; //ENUM
  public tyreLocation = TyreLocation; //ENUM
  public isLoading: boolean = false;
  public isAssigningServiceProvider = false;
  private incidentSaveRequest: Observable<Incident> | null = null;
  public get isSavingOrAssigning(): boolean {
    return this.incidentSaveRequest !== null || this.isAssigningServiceProvider;
  }
  public isLoadingTruckDrivers: boolean = false;
  public isLoadingFleetVehicles: boolean = false;
  public isBreakdownDetailsPanelActive: boolean = true; // Controls the visibility of the breakdown details panel
  public isSavedLocationNameModalVisible = false;
  public isCancelIncidentModalVisible = false;
  public isAddDriverModalVisible = false;
  public isAddVehicleModalVisible = false;
  public isAddFleetModalVisible = false;
  public isCloseIncidentModalVisible = false;
  public isUploadModalVisible = false;
  public isDelayDrawerVisible = false;
  public delayMinutes: number = 0;
  public delayReason: string = '';
  public isIncidentCompleteModalVisible: boolean = false;
  public isCompleteWithoutWorkReportConfirmModalVisible: boolean = false;
  public editOrderNumber: boolean = false;
  public isWaGroupOpenerModalVisible = false;
  public waGroupOpenerLink = '';
  public waGroupOpenerEtaOverride: number | null = null;
  public waGroupOpenerIsFleet = false;
  public waGroupOpenerServiceProviderId: number | null = null;
  
  public selectedCancelReasonId: number | null = null;
  public fleetSearchText: string = '';
  public isLoadingFleetGroupName: boolean = false;
  public truckDrivers: Driver[] = [];
  public fleetVehicles: Vehicle[] = [];
  public transporters: Transporter[] = [];
  public filteredTransporters: string[] = [];
  public breakdownTechnicians: BreakdownTechnician[] = [];
  public currentStep: number = 0;
  public fleetClient: FleetClient;
  public selectedPricing: PriceTemplate;
  public canNotifyDriver = true; //TODO: Determine if we can notify the driver
  public gFuncs = Globals.Functions;

  private geocoder = new google.maps.Geocoder();

  public locationLink: string = '';

  private locationChangedSinceLastSave: boolean = false;

  private incidentUpdateSubscription: Subscription;
  private serviceProviderStatusUpdateSubscription: Subscription;
  private breakdownTechnicianIncidentStatusUpdateSubscription: Subscription;

  constructor(
    public incidentService: IncidentService, 
    private route: ActivatedRoute, 
    private notification: NotificationService,
    public sharedDataService: SharedDataService,
    public authService: AuthService,
    private clientService: ClientService,
    private quoteService: QuoteService,
    private router: Router,
    private location: Location,
    private whatsappService: WhatsappService,
    private reportService: ReportService,
    private spService: ServiceProviderService
  ) {}

  private hydrateIncident(incident: Incident): Incident {
    return Object.assign(new Incident(), incident);
  }

  ngOnInit(): void {
    this.loadTransporters();

    this.route.params.subscribe(params => {
      
      const incidentId = +params['incidentId'];
      this.chatIncidentId = Number.isFinite(incidentId) ? incidentId : 0;
      this.whatsappService.currentIncidentId.set(incidentId);
      if (this.incident && this.incident.incidentId === incidentId) {
        // If the incident is already loaded, no need to fetch it again
        
        return;
      }
      this.isLoading = true;
      if (incidentId) {
        
        const searchedIncident = this.incidentService.openIncidents().find(incident => incident.incidentId === incidentId);
        
        //if found, no in list, no need to fetch from server
        if (searchedIncident) {
          console.log("Incident found in list, no need to fetch from server");
          
          this.incident = this.hydrateIncident(searchedIncident);
          this.checkDefaultValues();
          this.addOperatorToIncident();
          this.getFleetVehicles();
          this.getFleetTruckDrivers();
          this.isLoading = false;
          
        } 
        else {
          this.incidentService.getIncident(incidentId).subscribe(incident => {
            console.log("Fetched incident from server:");
            this.incident = this.hydrateIncident(incident);
            this.checkDefaultValues();
            this.addOperatorToIncident();
            this.getFleetTruckDrivers();
            this.getFleetVehicles();
            this.isLoading = false;
            //this.incidentService.operatorIncidents.push(incident);
            this.incidentService.updateOrAddIncidentToList(incident);
          });
        }

      } else {
        // If no incidentId is provided, we are creating a new incident
        if (this.sharedDataService.selectedMessages && this.sharedDataService.selectedMessages.length > 0) { 
          console.log("ADDING", this.sharedDataService.selectedMessages);
          
          this.incident = this.hydrateIncident(JSON.parse(this.sharedDataService.selectedMessages[0].extendedObject!) as Incident);
        }
        else if (this.sharedDataService.newIncident) {
          this.incident = this.hydrateIncident(this.sharedDataService.newIncident);
        }
        else {
          this.incident = new Incident();
        }
        
        
        this.checkDefaultValues();
        this.isLoading = false;
      }
   
       
      
      
    });
    //subscribe to incident updates
    this.incidentUpdateSubscription = this.incidentService.$incidentUpdate.subscribe((updatedIncident: Incident) => {
      console.log("We have received an incident update emit, but need to check if we are going to update");
      console.log(updatedIncident);
      
      if (this.incident.incidentId === updatedIncident.incidentId) {
        console.log("Received incident update via WebSocket for current incident:");
        console.log(this.incident);
        
        //if the location is different, display a notifiction and udpate the location
        //console.log(this.incident.location.latitude,updatedIncident.location.latitude,this.incident.location.longitude,updatedIncident.location.longitude);
        
        if (!this.incident.location || (updatedIncident.location.latitude && updatedIncident.location.longitude && (this.incident.location.latitude !== updatedIncident.location.latitude || this.incident.location.longitude !== updatedIncident.location.longitude))) {         
          // if (!this.incident.location ||this.incident.location.latitude !== updatedIncident.location.latitude || this.incident.location.longitude !== updatedIncident.location.longitude) {
            this.notification.showInfo("Breakdown location has been updated.");
            this.incident.location = updatedIncident.location;
            this.geocodeLocation(new google.maps.LatLng(this.incident.location.latitude, this.incident.location.longitude));
          // }
        }
        //update list of open docs
        for (let doc of updatedIncident.documents) { 
          if (!this.incident.documents.find(d => d.documentId === doc.documentId)) {
            this.incident.documents.push(doc);
          }
        }
        //update the service provider statusses
        for (let updatedClientLink of updatedIncident.incidentClients) {
          const existingLink = this.incident.incidentClients.find(link => link.clientId === updatedClientLink.clientId);
          if (existingLink) {
            existingLink.technicianStatusId = updatedClientLink.technicianStatusId;
            existingLink.hasSpAccepted = updatedClientLink.hasSpAccepted;
            existingLink.incidentTravelTimeMinutes = updatedClientLink.incidentTravelTimeMinutes;
            existingLink.incidentTravelTimeMinutesAdded = updatedClientLink.incidentTravelTimeMinutesAdded;
          }
        }
        this.incident.breakdownTechnicianDriverEta = updatedIncident.breakdownTechnicianDriverEta;
        console.log("We ahve updated everything, but we need to still merge please");
        this.incident = this.mergeIncomingIncident(this.incident, updatedIncident);
        if (updatedIncident.incidentDescription) {
          this.incident.incidentDescription = updatedIncident.incidentDescription;
        }
        console.log(this.incident);
        
        

        // if (updatedIncident.statusId > this.incident.statusId) {
        //   this.incident.statusId = updatedIncident.statusId;
        // }
        //check all other fields, if they are currently empty and we get new data, update them

        //this.incident = updatedIncident; //We will need to make sure that no values are overwritten that the user is currently editing
        //this.checkDefaultValues();
        // if (this.incident.location && !this.incident.location.description && this.incident.location.latitude && this.incident.location.longitude) { 
        //   this.geocodeLocation(new google.maps.LatLng(this.incident.location.latitude, this.incident.location.longitude));
        // }
      }
    });
    this.serviceProviderStatusUpdateSubscription = this.incidentService.$serviceProviderStatusUpdate.subscribe((statusUpdate: ServiceProviderStatusUpdate) => {
      console.log("Received Service Provider status update via WebSocket:");
      //check if service provider is linked to this incident
      if (this.incident.incidentClients.some(link => link.clientId === statusUpdate.clientId)) {
        //get the Service provider and update its status
        const spLink = this.incident.incidentClients.find(link => link.clientId === statusUpdate.clientId);
        if (spLink) {
          console.log("SP Found, updating");
          
          spLink.technicianStatusId = statusUpdate.serviceProviderStatusId;
        }
      }
    });
    this.breakdownTechnicianIncidentStatusUpdateSubscription = this.incidentService.$bdTechIncidentStatusUpdate.subscribe((statusUpdate: BDTechIncidentStatusUpdate) => {
      if (statusUpdate.statusId === 1 && this.incident.incidentId === statusUpdate.incidentId) { 
        //get thte breakdown technician off the incident incident.incidentOperators for type 5 and check userid
        let bdTech = this.incident.incidentOperators.find(op => op.userType === UserType.technician && op.operatorId === statusUpdate.userId);
        if (bdTech) {
          bdTech.isActive = true;
          this.notification.showInfo(`Breakdown technician ${bdTech.name} has accepted the breakdown.`);
          //replace item in array with new one to trigger change detection
          this.incident.incidentOperators = this.incident.incidentOperators.map(op => op.operatorId === bdTech!.operatorId ? bdTech! : op);
        }
      }
    });
  }

  private mergeIncomingIncident(incident: Incident, incomingIncident: Incident): Incident {
      console.log("Going to merge on Incident Component", incident);
      console.log("Incoming incident to merge", incomingIncident);
  
  
      // Merge basic fields
      if (!incident.clientId && incomingIncident.clientId) {
        incident.clientId = incomingIncident.clientId;
      }
      if (!incident.clientName && incomingIncident.clientName) {
        incident.clientName = incomingIncident.clientName;
      }
      if (!incident.incidentDescription && incomingIncident.incidentDescription) {
        incident.incidentDescription = incomingIncident.incidentDescription;
      }
      
      incident.incidentType = incomingIncident.incidentType;
      
      if (!incident.fleetVehicleOdoReading && incomingIncident.fleetVehicleOdoReading) {
        incident.fleetVehicleOdoReading = incomingIncident.fleetVehicleOdoReading;
      }
      // Merge driver information
      if (incomingIncident.driver) {
        if (!incident.driver.driverId && incomingIncident.driver.driverId) {
          incident.driver.driverId = incomingIncident.driver.driverId;
        }
        console.log(incident.driver.name + '---' + incomingIncident.driver.name );
        
        if ((!incident.driver.name || incident.driver.name.trim() === '') && incomingIncident.driver.name) {
          incident.driver.name = incomingIncident.driver.name;
          console.log("Merged driver name");
          
        }
        if (!incident.driver.phoneNumber && incomingIncident.driver.phoneNumber) {
          incident.driver.phoneNumber = incomingIncident.driver.phoneNumber;
        }
      }
  
      // Merge fleet vehicle information
      if (incomingIncident.fleetVehicle) {
        if (!incident.fleetVehicle.vehicleId && incomingIncident.fleetVehicle.vehicleId) {
          incident.fleetVehicle.vehicleId = incomingIncident.fleetVehicle.vehicleId;
        }
        if (!incident.fleetVehicle.registrationNumber && incomingIncident.fleetVehicle.registrationNumber) {
          incident.fleetVehicle.registrationNumber = incomingIncident.fleetVehicle.registrationNumber;
        }
        if (!incident.fleetVehicle.vinNumber && incomingIncident.fleetVehicle.vinNumber) {
          incident.fleetVehicle.vinNumber = incomingIncident.fleetVehicle.vinNumber;
        }
      }
  
      // Merge location information
      if (incomingIncident.location) {
        if (!incident.location.latitude && incomingIncident.location.latitude) {
          incident.location.latitude = incomingIncident.location.latitude;
        }
        if (!incident.location.longitude && incomingIncident.location.longitude) {
          incident.location.longitude = incomingIncident.location.longitude;
        }
        if (!incident.location.description && incomingIncident.location.description) {
          incident.location.description = incomingIncident.location.description;
        }
        if (!incident.location.descriptionShort && incomingIncident.location.descriptionShort) {
          incident.location.descriptionShort = incomingIncident.location.descriptionShort;
        }
      }
  
      // Merge tyre positions if current is empty or default
      if (incomingIncident.tyrePositions && incomingIncident.tyrePositions.length > 0 && incomingIncident.tyrePositions[0].tyreLocation && incomingIncident.tyrePositions[0].tyreNumber && incomingIncident.tyrePositions[0].size) {
        const hasOnlyDefaultTyre = incident.tyrePositions.length === 1 && 
                                   incident.tyrePositions[0].tyreLocation === TyreLocation.trailer &&
                                   incident.tyrePositions[0].tyreNumber === 0;
        if (incident.tyrePositions.length === 0 || hasOnlyDefaultTyre) {
          incident.tyrePositions = incomingIncident.tyrePositions;
        }
      }
  
      // Merge tyre brands if current has only default value
      if (incomingIncident.tyreBrands && incomingIncident.tyreBrands.length > 0) {
        const hasOnlyDefaultBrand = incident.tyreBrands.length === 1 && incident.tyreBrands[0] === -1;
        if (hasOnlyDefaultBrand) {
          incident.tyreBrands = incomingIncident.tyreBrands;
        }
      }

      //replace flags
      incident.incidentActions = incomingIncident.incidentActions;
  
  
      console.log("Merged incoming incident into current incident ON INCIDENT COMPONENT:", incident);
      return incident;
    }

  ngOnDestroy(): void {
    if (this.incidentUpdateSubscription) {
      this.incidentUpdateSubscription.unsubscribe();
    }
    if (this.serviceProviderStatusUpdateSubscription) {
      this.serviceProviderStatusUpdateSubscription.unsubscribe();
    }
    if (this.breakdownTechnicianIncidentStatusUpdateSubscription) {
      this.breakdownTechnicianIncidentStatusUpdateSubscription.unsubscribe();
    }
    this.whatsappService.currentIncidentId.set(null);
  }

  private updateIncidentToAPI(loadServiceProviders: boolean = false) : Observable<Incident> | null {
    if (this.isSavingOrAssigning) return null;
    //define min to create a new incident
    if ((!this.incident.location || !this.incident.location.latitude || !this.incident.location.longitude) && (!this.incident.driver || !this.incident.driver.phoneNumber)) {
      //we cannot create an incident without these minimum details which in this case is a location OR driver details (so we can actually send the messages)
      return null;
    }


    this.isLoading = true;
    
    //If an invalid tyre location is given with an undefined size, remove it from the array
    this.incident.tyrePositions = this.incident.tyrePositions.filter(pos => pos.size !== undefined);
    //check if user is linked to incident operators, if not add them
    if (this.authService.user && !this.incident.incidentOperators.some(op => op.operatorId === this.authService.user?.userId)) {
      const incidentOperator = new IncidentOperator();
      incidentOperator.operatorId = this.authService.user?.userId;
      incidentOperator.userType = this.authService.user?.userType;
      incidentOperator.isActive = true;
      this.incident.incidentOperators.push(incidentOperator);
    }
    //values
    if (this.incident.incidentId == undefined) { 
      this.incident.incidentId = 0;
    }

    //check if client is linked to incident clients, if not add them
    if (this.authService.user && this.authService.user.clientId && !this.incident.incidentClients.some(link => link.clientId === this.authService.user?.clientId)) {
      const incidentClient = new IncidentClientLink();
      incidentClient.clientId = this.authService.user.clientId;
      incidentClient.connectionTypeId = 2; // Set default connection type - call centre.
      incidentClient.isActive = true;
      this.incident.incidentClients.push(incidentClient);
    }


    let saved = false;
    const saveRequest: Observable<Incident> = this.incidentService.updateIncident(this.incident!)
    .pipe(
      tap({
        next: (updatedIncident) => {
          //check to update browser
          const newUrl = window.location.pathname.replace("new", updatedIncident.incidentId.toString());
          this.location.replaceState(newUrl);
          
          this.incident = this.hydrateIncident(updatedIncident);
          this.whatsappService.currentIncidentId.set(updatedIncident.incidentId); //to ensure whatsapp messages are linked correctly

          
          this.checkDefaultValues();
          //check if incident exists in array
          const index = this.incidentService.openIncidents().findIndex(incident => incident.incidentId === this.incident?.incidentId);
          if (index !== -1) {
            this.incidentService.openIncidents()[index] = this.incident; //Is this really necessary with reference types and websocket update?
          } else {
            this.incidentService.openIncidents().push(this.incident);
          }
          
          this.notification.showSuccess('Breakdown updated successfully');
          
          if (loadServiceProviders && this.authService.isClientCallCentre) {
            
            this.load5ServiceProviders();
          }
          this.runQuotesForAllServiceProviders();

          if (this.locationChangedSinceLastSave && this.getServiceProviders().length > 0) {
            this.runServiceProviderInformation(true);
            this.locationChangedSinceLastSave = false;
          }
          
          //if there is an open message, zero it and update the whatsapp
          if (this.sharedDataService.selectedMessages && this.incident.statusId >= 2) {
            
            for (let msg of this.sharedDataService.selectedMessages) {
              //TODO: Assign to incident
              this.incidentService.assignWhatsappMessageToIncident(this.incident.incidentId, msg.whatsappMessageId).subscribe({
                next: (incident: Incident) => {
                  this.incidentService.updateOrAddIncidentToList(incident);
                  //update the messsage incident
                  this.whatsappService.changeMessageIncident(msg.whatsappMessageId, incident.incidentId!, incident.incidentNumber!);
                },
                error: (error) => {
                  this.notification.handleError(error, 'Error assigning message to breakdown');
                }
              });   
            }
            this.sharedDataService.selectedMessages = [];
          }
          saved = true;
        },
        error: (error) => {
          this.notification.showError('Failed to update breakdown');
        }
      }),
      finalize(() => {
        if (this.incidentSaveRequest === saveRequest) this.incidentSaveRequest = null;
        this.isLoading = this.isAssigningServiceProvider;
        // Default fleet selection may have been blocked while this save was active.
        if (saved && !this.isAssigningServiceProvider && !this.incident.clientId) this.checkDefaultValues();
      }),
      // Assign can wait for this save without sending another PUT or repeating its side effects.
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.incidentSaveRequest = saveRequest;
    return saveRequest;
  }

  private loadTransporters(): void {
    this.clientService.getTransporters().subscribe({
      next: (data) => {
        this.transporters = data;
        this.filteredTransporters = data.map(t => t.transporterName);
      },
      error: () => {}
    });
  }

  public onTransporterInput(value: string): void {
    const upper = (value ?? '').toUpperCase();
    this.incident.transporterName = upper;
    this.filteredTransporters = this.transporters
      .map(t => t.transporterName)
      .filter(name => name.includes(upper));
  }

  private getFleetTruckDrivers() {
    if (this.incident.clientId && this.incident.clientId > 0 || 1===1) { //always run
      this.isLoadingTruckDrivers = true;
      this.clientService.fleetTruckDrivers(0).subscribe({ //always ask for all drivers (0), otherwise add clietnid
        next: (drivers) => {
          this.truckDrivers = drivers;
          this.isLoadingTruckDrivers = false;
          //check to see if we can select a truck driver
          if (!this.incident.driver.driverId && this.incident.driver.phoneNumber) { 
            const matchingDriver = this.truckDrivers.find(d => d.phoneNumber === this.incident.driver.phoneNumber);
            if (matchingDriver) {
              this.incident.driver.driverId = matchingDriver.driverId;
              //save incident to get incidentId - to send whatsapp message
              if (!this.incident.incidentId || this.incident.incidentId === 0) {
                this.updateIncidentToAPI()?.subscribe();
              }
            }
          }
          
        },
        error: (error) => {
          this.isLoadingTruckDrivers = false;
          this.notification.showError('Failed to load truck drivers');
        }
      });
    }
  }

  //REGION Helper Functions

  private getServiceProviderBreakdownTechnicians() {
    if (this.hasSelectedServiceProvider() && (!this.incident.breakdownTechnician || !this.incident.breakdownTechnician.driverId) && (this.canSelectBreakdownTechnician()) ) {
      //we have and active service provider, but not a breakdown technician assigned
      this.clientService.breakdownTechnicians(this.getSelectedServiceProviderClientId()!).subscribe({
        next: (technicians) => {
          this.breakdownTechnicians = technicians;
        },
        error: (error) => {
          this.notification.showError('Failed to load breakdown technicians from service provider');
        }
      });
    }
  }

  public hasBDTechAcceptedIncident(): boolean {
    //first get the bdtech then return the isActive field
    let bdTech = this.incident.incidentOperators.find(op => op.userType === UserType.technician);
    return bdTech ? (bdTech.isActive ?? false) : false;
  }

  public hasValidDriverCellNumber(): boolean {
    return this.incident.driver && !!this.incident.driver.phoneNumber && /^0\d{9}$/.test(this.incident.driver.phoneNumber);
  }

  public onDriverCellBlur(): void {
    if (this.incident.driver?.phoneNumber) {
      this.incident.driver.phoneNumber = Globals.Functions.normalizeSAPhoneNumber(this.incident.driver.phoneNumber);
    }
  }

  public isFleetLinkedToLoggedInClient(): boolean {
    return this.sharedDataService.clientLinks.some(link => link.clientId === this.incident.clientId && link.connectionTypeId === 1);
  }

  public getOtherMainCallcentre(incident: Incident): IncidentClientLink | undefined {
    return incident.incidentClients.filter(link => link.connectionTypeId === 2 && link.isActive===false && link.clientId !== this.authService.user?.clientId)[0];
  }

  //ENDREGION

  public onDriverChanged(driverId: number) {
    this.incident.driver.driverId = driverId;
    const selectedDriver = this.truckDrivers.find(d => d.driverId === driverId);
    if (selectedDriver) {
      this.incident.driver.name = selectedDriver.name;
      this.incident.driver.phoneNumber = selectedDriver.phoneNumber;
    } else {
      this.incident.driver.name = '';
      this.incident.driver.phoneNumber = '';
    }
    //save incident to get incidentId - to send whatsapp message
    if (!this.incident.incidentId || this.incident.incidentId === 0) {
      this.updateIncidentToAPI()?.subscribe();
    }
  }

  public onSelectDriverFromAutocomplete(event: any) {
    const phoneNumber = event.nzValue;
    const selectedDriver = this.truckDrivers.find(d => d.phoneNumber === phoneNumber);
    if (selectedDriver) {
      this.incident.driver.driverId = selectedDriver.driverId;
      this.incident.driver.name = selectedDriver.name;
      this.incident.driver.phoneNumber = selectedDriver.phoneNumber;
      
      //save incident to get incidentId - to send whatsapp message
      if (!this.incident.incidentId || this.incident.incidentId === 0) {
        this.updateIncidentToAPI()?.subscribe();
      }
    }
  }

  public onVehicleRegInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const upper = Globals.Functions.normalizeVehicleReg(input.value);
    if (input.value !== upper) {
      input.value = upper;
    }
    this.incident.fleetVehicle.registrationNumber = upper;
  }

  public onSelectVehicleFromAutocomplete(event: any) {
    const registrationNumber = event.nzValue;
    const selectedVehicle = this.fleetVehicles.find(v => v.registrationNumber === registrationNumber);
    if (selectedVehicle) {
      this.incident.fleetVehicle.vehicleId = selectedVehicle.vehicleId;
      this.incident.fleetVehicle.registrationNumber = selectedVehicle.registrationNumber;
      this.incident.fleetVehicle.vinNumber = selectedVehicle.vinNumber;

      //save incident to get incidentId - to send whatsapp message
      if (!this.incident.incidentId || this.incident.incidentId === 0) {
        this.updateIncidentToAPI()?.subscribe();
      }
    } else {
      this.incident.fleetVehicle.vehicleId = 0;
    }
  }

  private getFleetVehicles() {
    if (this.incident.clientId && this.incident.clientId > 0 || 1==1) { //always run
      this.isLoadingFleetVehicles = true;
      this.clientService.fleetVehicles(0).subscribe({ //always ask for all vehicles
        next: (vehicles) => {
          this.fleetVehicles = vehicles;
          this.isLoadingFleetVehicles = false;
          //check to match fleet vehicle
          if (!this.incident.fleetVehicle.vehicleId && this.incident.fleetVehicle.registrationNumber) {
            const matchingVehicle = this.fleetVehicles.find(v => v.registrationNumber === this.incident.fleetVehicle.registrationNumber);
            if (matchingVehicle) {
              this.incident.fleetVehicle.vehicleId = matchingVehicle.vehicleId;
              //save incident to get incidentId - to send whatsapp message
              if (!this.incident.incidentId || this.incident.incidentId === 0) {
                this.updateIncidentToAPI()?.subscribe();
              }
            }
          }
        },
        error: (error) => {
          this.isLoadingFleetVehicles = false;
          this.notification.showError('Failed to load fleet vehicles');
        }
      });
    }
  }

  public onVehicleChanged(vehicleId: number) {
    this.incident.fleetVehicle.vehicleId = vehicleId;
    const selectedVehicle = this.fleetVehicles.find(v => v.vehicleId === vehicleId);
    if (selectedVehicle) {
      this.incident.fleetVehicle.registrationNumber = selectedVehicle.registrationNumber;
      this.incident.fleetVehicle.vinNumber = selectedVehicle.vinNumber;
    } else {
      this.incident.fleetVehicle.registrationNumber = '';
      this.incident.fleetVehicle.vinNumber = '';
    }
    //save incident to get incidentId - to send whatsapp message
    if (!this.incident.incidentId || this.incident.incidentId === 0) {
      this.updateIncidentToAPI()?.subscribe();
    }
  }

  public onSelectBreakdownTechnician(driverId: number) {
    const selectedTechnician = this.breakdownTechnicians.find(tech => tech.driverId === driverId);
    if (selectedTechnician) {
      this.incident.breakdownTechnician = selectedTechnician;
      this.isLoading = true;
      this.incidentService.assignBreakdownTechnicianToIncident(this.incident.incidentId, driverId, this.incident).subscribe({
        next: (updatedIncident) => {
          this.incident = this.hydrateIncident(updatedIncident);
          this.isLoading = false;
          this.notification.showSuccess(`Breakdown technician ${selectedTechnician.name} has been assigned to the breakdown.`);
        },
        error: (error) => {
          this.isLoading = false;
          this.notification.handleError(error, 'Failed to assign breakdown technician to breakdown');
        }
      });
    }
  }

  public onClickSendTechRequest(driverId: number) {
    this.onSelectBreakdownTechnician(driverId);
  }

  public onClickDriverAsked() {
    if (this.isSavingOrAssigning) return;
    this.incident.statusId = 6;
    this.updateIncidentToAPI()?.subscribe();
  }

  public onClickDriverHasAccepted(driverId?: number) {
    if (this.isSavingOrAssigning) return;
    this.incident.statusId = 7;
    if (driverId !== undefined) {
      this.incident.incidentOperators.find(op => op.operatorId === driverId && op.userType === UserType.technician)!.isActive = true;
    }
    this.updateIncidentToAPI()?.subscribe();
  }

  public onClickDriverHasArrived() {
    if (this.isSavingOrAssigning) return;
    this.incident.statusId = 8;
    this.updateIncidentToAPI()?.subscribe();
  }

  public addOperatorToIncident() {
    if (this.incident.incidentOperators.some(op => op.operatorId === this.authService.user?.userId)) {
      return;
    }
    this.incidentService.assignIncidentToOperator(this.incident.incidentId).subscribe({
        next: (updatedIncident) => {
          this.incident = this.hydrateIncident(updatedIncident);
          //replace the incident in the list with the updated one
          if (this.incident) {
            const index = this.incidentService.openIncidents().findIndex(incident => incident.incidentId === this.incident?.incidentId);
            if (index !== -1) {
              this.incidentService.openIncidents()[index] = updatedIncident;
            }
          }
        },
        error: (error) => {
          this.notification.handleError(error, 'Failed to assign breakdown to operator');
        }
      });
  }

  public addClientToIncident(client: ClientLink): void {
    if (!this.incident.incidentClients.some(link => link.clientId === client.clientId)) {
      this.incident.incidentClients.push(new IncidentClientLink(client));
      this.incident.statusId = 3; //Handed over to callcentre
      this.updateIncidentToAPI()?.subscribe();
      
    }
  }

  public onChangeStep(index: number): void {
    this.currentStep = index;

    // if (this.currentStep === 0 && this.incident.clientId) {
    //   this.getFleetVehicles();
    //   this.getFleetTruckDrivers();
    // }

  }

  // public technicianDrivingTimes: { [key: number]: string } = {};
  // public technicianServiceProviderDrivingTimes: { [key: number]: string } = {};

  // public onClickCalculateDrivingTime() {
  //   const incidentLocation: google.maps.LatLngLiteral = {
  //     lat: this.incident.location.latitude,
  //     lng: this.incident.location.longitude,
  //   };
  //   if (this.sharedDataService.breakdownTechnicians.length > 0) {
  //     //calculate our location to incident
  //     let thisClient = this.sharedDataService.clientLinks.filter(link => link.clientId === this.authService.user?.clientId)[0];

  //     const userLocation: google.maps.LatLngLiteral = {
  //           lat: thisClient.latitude,
  //           lng: thisClient.longitude,
  //         };
  //     console.log(userLocation);
  //     this.locationService.calculateDrivingTimeAndLengthFormatted(userLocation, incidentLocation).then(result => {
  //       for (let item of this.sharedDataService.breakdownTechnicians) {
  //         this.technicianDrivingTimes[item.driverId] = result?.distance + "; " + result?.duration || "Unknown Driving Time";
  //       }
  //     });
  //   }
  //   if (this.sharedDataService.getTechnicianServiceProviders.length > 0) {
  //     for (let client of this.sharedDataService.getTechnicianServiceProviders) {
  //       const spLocation: google.maps.LatLngLiteral = {
  //         lat: client.latitude,
  //         lng: client.longitude,
  //       };
  //       this.locationService.calculateDrivingTimeAndLengthFormatted(spLocation, incidentLocation).then(result => {
  //         this.technicianServiceProviderDrivingTimes[client.clientId] = result?.distance + "; " + result?.duration || "Unknown Driving Time";
  //       });
  //     }
  //   }

  // }

  // public technicianStockLevels: { [key: number]: number } = {};
  // public onClickLiveStockCheck() {
  //   let dataObj = new ClientStockRequest();
  //   dataObj.gencode = this.incident.tyrePositions[0].size;
  //   dataObj.stock = [];
  //   dataObj.tyreBrandIds = this.incident.tyreBrands;

  //   //add technician service provider stock levels
  //   if (this.sharedDataService.getTechnicianServiceProviders.length > 0) {
  //     for (let client of this.sharedDataService.getTechnicianServiceProviders) {
  //       const stockItem = new ClientStock();
  //       stockItem.clientId = client.clientId;
  //       stockItem.gencode = dataObj.gencode;
  //       stockItem.soh = 0; // Default stock on hand
  //       dataObj.stock.push(stockItem);
  //     }
  //   }
  //   //add own stock
  //   const stockItem = new ClientStock();
  //   stockItem.clientId = this.authService.user?.clientId!;
  //   stockItem.gencode = dataObj.gencode;
  //   stockItem.soh = 0; // Default stock on hand
  //   dataObj.stock.push(stockItem);
  //   this.stockService.getStockList(dataObj).subscribe({
  //     next: (stockList) => {
  //       //clear technician stock levels
  //       this.technicianStockLevels = {};
  //       for (let stockItem of stockList.stock) {
  //         this.technicianStockLevels[stockItem.clientId] = stockItem.soh;
  //       }
  //       console.log(this.technicianStockLevels);

  //     }
  //   })
  // }

  // public addTechnicianDriverToIncident(driverId: number) {
  //   let breakdownTechnician = this.sharedDataService.breakdownTechnicians.find(tech => tech.driverId === driverId)!;   
      
  //   this.incidentService.requestBreakdownTech(this.incident.incidentId, driverId).subscribe({
  //     next: incident => {
  //       this.incident = incident;
  //       this.notification.showSuccess(`Breakdown technician ${breakdownTechnician.name} has been assigned to the incident.`);
  //     },
  //     error: (error) => {
  //       this.notification.handleError(error, 'Failed to assign breakdown technician to incident');
  //     }
  //   });
  // }

  // public addTechnicianServiceProviderToIncident(client: ClientLink) {
  //   if (!this.incident.incidentClients.some(link => link.clientId === client.clientId)) {
  //     this.incident.incidentClients.push(new IncidentClientLink(client));
  //     this.incident.statusId = 4; //Handed over to service provider
  //     this.updateIncidentToAPI();
      
  //   }
  // }


  private checkDefaultValues(): void {
    this.sharedDataService.metadataLoaded.pipe(
      take(1)
    ).subscribe(() => {
      this.setDefaultCallcentreOperator();
    });

    // Wait for metadata to finish loading
    this.sharedDataService.isLoadingMetaData.pipe(
      filter(loading => loading === false), //only proceed when loading is false, meaning only TAKE 1 when loading is false
      take(1) //This will auto-unsubscribe after the first emission or completion, behavioustsubject remembers last value and "emit" as soon as someone subscribes
    ).subscribe({next: _ => {
      this.performCheckDefaultValues();
    }});
  }

  private setDefaultCallcentreOperator(): void {
    if (!this.incident.incidentId && this.incident.callcentreOperatorId == null) {
      this.incident.callcentreOperatorId = this.authService.callcentreOperator?.userId ?? null;
    }
  }

  private performCheckDefaultValues(): void {
    this.setDefaultCallcentreOperator();
    
    //Check fleet id
    if (!this.incident.clientId) {
      if (this.sharedDataService.getFleets.length === 1 && !this.authService.isClientCallCentre) {
        this.onSelectFleet(this.sharedDataService.getFleets[0]);
      }
    }
    //check driverid
    if (!this.incident.orderNumber) {
      this.editOrderNumber = true;
    }
    //statusId
    if (!this.incident.statusId || this.incident.statusId === 0) {
      this.incident.statusId = 0; //New
    }

    //tyre positions
    if (!this.incident.tyrePositions) {
      this.incident.tyrePositions = [];
    }
    if (this.incident.tyrePositions.length === 0) {
      this.incident!.tyrePositions.push(new IncidentTyre(TyreLocation.trailer, 0));
    }
    else {
      for (let tyre of this.incident.tyrePositions) {
        if (!tyre.tyreNumber) {
          tyre.tyreNumber = 0;
        }
        if (!tyre.tyreLocation) {
          tyre.tyreLocation = TyreLocation.trailer;
        }
        // if (!tyre.size) {
        //   tyre.size = '31565225';
        // }
      }
    }
    //incident clients
    if (!this.incident.incidentClients) {
      this.incident.incidentClients = [];
    }
    //incident operators
    if (!this.incident.incidentOperators) {
      this.incident.incidentOperators = [];
    }
    
    //tyre brands
    // if (!this.incident.tyreBrands || this.incident.tyreBrands.length === 0) {
    //   console.log("Setting default tyre brand to -1 (import)");
      
    //   this.incident.tyreBrands = [-1]; // defaults to import
    // }
    //location
    if (this.incident.location && this.incident.location.latitude && this.incident.location.longitude && !this.incident.location.description) {
      const latLng = new google.maps.LatLng(this.incident.location.latitude, this.incident.location.longitude);
      this.geocodeLocation(latLng);
    }
    //init empty driver if not set
    if (!this.incident.driver) {
      this.incident.driver = new Driver();
    }
    //init empty vehicle if not set
    if (!this.incident.fleetVehicle) {
      this.incident.fleetVehicle = new Vehicle();
    }
    //init other empty vals
    if (!this.incident.incidentDescription) {
      this.incident.incidentDescription = '';
    }
    // if (!this.incident.fleetVehicleOdoReading) {
    //   this.incident.fleetVehicleOdoReading = 0;
    // }
    
    //Steps default
    if (this.incident.statusId < 1) {
      this.currentStep = 0;
    }
    else if (this.incident.statusId < 7) { 
      this.currentStep = 1;
    } 
    else if (this.incident.statusId <=10) {
      this.currentStep = 2;
    }
    else {
      this.currentStep = 3;
    }

    if (!this.authService.isClientCallCentre && !this.authService && this.currentStep > 1) {
      this.currentStep = 0;
    }
    
    //check if we need to load the Breakdown Technicians, early exit logic exists in the function
    this.getServiceProviderBreakdownTechnicians();
  }

  public getCallCentre(): ClientLink | undefined {
     return this.incident.incidentClients.find(link => link.connectionTypeId === 2);
  }

  public getCallcentreIsActiveForSp(spClientId: number): boolean | null | undefined {
    const spLink = this.sharedDataService.serviceProviderLinks.find(sp => sp.clientId === spClientId);
    if (!spLink?.callcentreId) return undefined;
    const ccLink = this.incident.incidentClients.find(link => link.clientId === spLink.callcentreId);
    return ccLink?.isActive;
  }

  public isShowingInactiveServiceProviders: boolean = false;
  public hasInactiveServiceProviders(): boolean {
    return this.incident.incidentClients.some(link => link.connectionTypeId === 3 && link.isActive === false);
  }
  
  public getServiceProviders(): IncidentClientLink[] {
    const visibleSpIds = new Set(this.sharedDataService.serviceProviderLinks.map(sp => sp.clientId));
    return this.incident.incidentClients.filter(link =>
      link.connectionTypeId === 3 &&
      visibleSpIds.has(link.clientId) &&
      (this.isShowingInactiveServiceProviders === true || link.isActive === true || link.isActive === null || link.isActive === undefined)
    ).slice();
  }

  public getDisplayServiceProviders(): IncidentClientLink[] {
    const all = this.getServiceProviders();
    const direct: IncidentClientLink[] = [];
    const byCallcentre = new Map<number, IncidentClientLink>();

    for (const sp of all) {
      if (this.isSpDirectlyLinked(sp.clientId)) {
        direct.push(sp);
      } else {
        const spLink = this.sharedDataService.serviceProviderLinks.find(l => l.clientId === sp.clientId);
        const ccId = spLink?.callcentreId ?? sp.clientId;
        const existing = byCallcentre.get(ccId);
        if (!existing || this.sortByDistance(sp, existing) < 0) {
          byCallcentre.set(ccId, sp);
        }
      }
    }

    return [...direct, ...Array.from(byCallcentre.values())];
  }

  public isSpDirectlyLinked(clientId: number): boolean {
    const spLink = this.sharedDataService.serviceProviderLinks.find(sp => sp.clientId === clientId);
    if (!spLink) return false;
    return spLink.callcentreId === null || spLink.callcentreId === this.authService.user?.clientId;
  }

  public getSpCallcentreName(clientId: number): string | null {
    const spLink = this.sharedDataService.serviceProviderLinks.find(sp => sp.clientId === clientId);
    return spLink ? spLink.callcentreName : null;
  }

  public hasSelectedServiceProvider(): boolean {
    return this.incident.incidentClients.some(link => link.connectionTypeId === 3 && link.isActive === true);
  }

  public amSelectedServiceProvider(): boolean {
    const spLink = this.incident.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true);
    if (spLink && this.authService.user && this.authService.user.clientId === spLink.clientId) {
      return true;
    }
    else {
      return false;
    }
  }

  public getSelectedServiceProviderClientId(): number | null {
    const spLink = this.incident.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true);
    return spLink ? spLink.clientId : null;
  }
  
  public canSelectBreakdownTechnician(): boolean {
    const spLink = this.incident.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true);
    let linkCheck = false;
    if (spLink && spLink.canSelectBreakdownTechnician && this.authService.isClientCallCentre) {
      linkCheck = true;
    }

    return this.authService.hasStoredClaim('SBD') || linkCheck;
  }

  //#region Tyre Position Selection

  public getTyresInPosition(loc: TyreLocation): IncidentTyre[] {
    return this.incident.tyrePositions.filter(t => t.tyreLocation === loc);
  }

  public tyrePositionFillColor(tyreLocation: TyreLocation, tyreNumber: number): string {
    if (this.incident!.tyrePositions.find(t => t.tyreLocation === tyreLocation && t.tyreNumber === tyreNumber)) {
      return "#FF0000"; 
    }
    return "#999999";
  }

  public onClickTyrePosition(tyreLocation: TyreLocation, tyreNumber: number): void {
    //find the tyre in the incident's tyrepositions
    const tyre = this.incident!.tyrePositions.find(t => t.tyreLocation === tyreLocation && t.tyreNumber === tyreNumber);
    if (tyre) {
      // If the tyre is found, it means it's selected, so we deselect it by removing it from the array
      this.incident!.tyrePositions = this.incident!.tyrePositions.filter(t => t !== tyre);
    } 
    else {
      //not found, so we need to add it to array
      //if there is already a tyre in the location with number = 0, update exisiting to current number
      const existingTyre = this.incident!.tyrePositions.find(t => t.tyreLocation === tyreLocation && t.tyreNumber === 0);
      if (existingTyre) {
        existingTyre.tyreNumber = tyreNumber;
      } else {
        this.incident!.tyrePositions.push(new IncidentTyre(tyreLocation, tyreNumber));
      }

    }
    
  }

  public onClickTyreLocation(tyreLocation: TyreLocation): void {
    const existingIndex = this.incident!.tyrePositions.findIndex(t => t.tyreLocation === tyreLocation);
    if (existingIndex !== -1) {
      // Already selected — toggle off only if more than one position is active
      if (this.incident!.tyrePositions.length > 1) {
        this.incident!.tyrePositions.splice(existingIndex, 1);
      }
    } else {
      // Not yet selected — add it, copying size and qty from the first position
      const refTyre = this.incident!.tyrePositions[0];
      const newTyre = new IncidentTyre(tyreLocation, 0);
      if (refTyre?.size) newTyre.size = refTyre.size;
      if (refTyre?.qty) newTyre.qty = refTyre.qty;
      this.incident!.tyrePositions.push(newTyre);
    }
  }

  private syncTyrePositions(): void {
    if (this.incident!.tyrePositions.length <= 1) return;
    const ref = this.incident!.tyrePositions[0];
    for (let i = 1; i < this.incident!.tyrePositions.length; i++) {
      this.incident!.tyrePositions[i].size = ref.size;
      this.incident!.tyrePositions[i].qty = ref.qty;
    }
  }

  public hasTyreInLocation(tyreLocation: TyreLocation): boolean {
    return this.incident!.tyrePositions.find(t => t.tyreLocation === tyreLocation) !== undefined;
  }

  //#endregion Tyre Position Selection

  public onSelectIncidentType(type: IncidentType): void {
    this.incident!.incidentType = type;
  }

  public onSelectTyreType(brandId: number, brandName: string = ""): void {
    //check if the brandid exists in this.incident!.tyreBrands and remove if so, otherwise add it
    const existingIndex = this.incident!.tyreBrands.findIndex(t => t === brandId);
    if (existingIndex !== -1) {
      this.incident!.tyreBrands.splice(existingIndex, 1);
    } else {
      this.incident!.tyreBrands.push(brandId);
    }

  }

  public isTyreTypeSelected(brandId: number): boolean {
    return this.incident!.tyreBrands.some(t => t === brandId);
  }

  public hasSelectedTyreLocation(): boolean {
    return this.incident.tyrePositions?.some(t => t.tyreLocation > 0) ?? false;
  }

  public hasSelectedTyreType(): boolean {
    return (this.incident.tyreBrands?.length ?? 0) > 0;
  }

  public onSelectFleetById(clientId: number | null) {
    if (!clientId) {
      // Handle clear selection
      return;
    }
    const fleet = this.sharedDataService.getFleets.find(f => f.clientId === clientId);
    if (fleet) {
      this.onSelectFleet(fleet);
    } else {
      console.warn('Fleet not found for clientId:', clientId);
    }
  }

  public onSelectFleet(link: ClientLink) {
    if (this.isSavingOrAssigning) return;
    console.log('onSelectFleet called with:', link);
    this.incident.clientId = link.clientId;
    this.incident.clientName = link.clientName;
    //remove previous fleet if any
    this.incident.incidentClients = this.incident.incidentClients.filter(c => c.connectionTypeId !== 1);
    //add new fleet as incident client
    console.log("Adding fleet to incident clients: ", link);    
    const fleetLink = new IncidentClientLink(link);
    fleetLink.isActive = true;
    this.incident.incidentClients.push(fleetLink);
    this.getFleetTruckDrivers();
    this.getFleetVehicles();
    
    // Save incident with status 1 after fleet selection
    if (!this.incident.statusId || this.incident.statusId === 0) {
      this.incident.statusId = 1;
    }
    console.log('Calling updateIncidentToAPI with incident:', this.incident);
    this.updateIncidentToAPI()?.subscribe();
  }

  public onMapSelected(location: IncidentLocation | null): void {
    if (location !== null) {
      this.incident.location = location;
      this.locationChangedSinceLastSave = true;
    }
  }

  public engineFillOpacity(): number {
    return this.incident!.incidentType === IncidentType.Both ? 1 : 0;
  }

  private geocodeLocation(latLng: google.maps.LatLng): void {
    this.geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        // Successfully found location details
        this.incident.location.description = results[0].formatted_address;
        var descriptionShort = '';
        for (var addItem of results[0].address_components) {
          if (addItem.types.includes('sublocality_level_1')) {
            descriptionShort += addItem.long_name + ', ';
          }
          if (addItem.types.includes('locality')) {
            descriptionShort += addItem.long_name + ', ';
          }
          if (addItem.types.includes('administrative_area_level_1')) {
            descriptionShort += addItem.short_name;
          }
        }   
        this.incident.location.descriptionShort = descriptionShort;
        console.log('Geocoding Result:', this.incident.location.description, descriptionShort);
      } 
    });
  }

  private geocodeAddress(address: string): Promise<[number | null, number | null]> {
    return new Promise((resolve) => {
      this.geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          resolve([location.lat(), location.lng()]);
        } else {
          console.error(`Geocoding API failed with status: ${status}`);
          resolve([null, null]);
        }
      });
    });
  }

  private async getLatLongFromUrl(url: string): Promise<[number | null, number | null]> {
    try {
      // Decode any Unicode escapes (e.g. \u0026 -> &)
      url = url.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

      // Expand short URLs (e.g. maps.app.goo.gl) via a backend proxy, since
      // browser CORS prevents following cross-origin redirects directly.
      if (/goo\.gl|maps\.app\.goo\.gl/.test(url)) {
        try {
          const result = await firstValueFrom(
            this.incidentService.expandUrl(url)
          );
          url = result.url;
        } catch (e) {
          console.error('Failed to expand short URL via backend:', e);
          return [null, null];
        }
      }

      // Pattern A: '?q=' or '&q=' with coordinates
      const matchQ = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (matchQ) return [parseFloat(matchQ[1]), parseFloat(matchQ[2])];

      // Pattern B: '@' parameter
      const matchAt = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (matchAt) return [parseFloat(matchAt[1]), parseFloat(matchAt[2])];

      // Pattern C: '!3d' and '!4d' (Protobuf format)
      const matchPb = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
      if (matchPb) return [parseFloat(matchPb[1]), parseFloat(matchPb[2])];

      // Pattern D: 'query=' parameter
      const matchQuery = url.match(/[?&]query=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (matchQuery) return [parseFloat(matchQuery[1]), parseFloat(matchQuery[2])];

      // Fallback: extract address string and geocode it
      const matchPlace = url.match(/\/place\/([^/]+)/);
      const matchSearch = url.match(/\/search\/([^/]+)/);
      const matchQAddress = url.match(/[?&]q=([^&]+)/);

      let address: string | null = null;
      if (matchPlace) address = matchPlace[1];
      else if (matchSearch) address = matchSearch[1];
      else if (matchQAddress) address = matchQAddress[1];

      if (address) {
        const cleanAddress = decodeURIComponent(address.replace(/\+/g, ' '));
        console.log(`Coordinates missing. Geocoding address: ${cleanAddress}`);
        return await this.geocodeAddress(cleanAddress);
      }

      console.warn(`Could not find coordinates or a valid address in URL: ${url}`);
      return [null, null];
    } catch (e) {
      console.error(`Error processing URL: ${e}`);
      return [null, null];
    }
  }

  public onClickSaveAndContinue() {
    if (this.isSavingOrAssigning) return;
    // Set status to 2 and save
    if (this.incident.statusId < 1) {
      this.incident.statusId = 1;
    }
    
    // this.isLoading = true;
    // this.incidentService.updateIncident(this.incident).subscribe({
    //   next: (updatedIncident) => {
    //     this.incident = updatedIncident;
    //     this.isLoading = false;
    //     this.notification.showSuccess('Incident saved successfully');
    //     // Move to second tab
    //     this.currentStep = 1;
    //   },
    //   error: (error) => {
    //     this.isLoading = false;
    //     this.notification.handleError(error, 'Failed to save incident');
    //   }
    // });
    
    //if we already have service providers, don't load more
    let mustLoad = !this.incident.incidentClients.some(link => link.connectionTypeId === 3);
    console.log("Must we load service providers?", mustLoad);
    
    this.updateIncidentToAPI(mustLoad)?.subscribe();
  }

  public onClickUpdateAndRegisterBreakdown() {
    if (this.isSavingOrAssigning) return;

    // if (this.incident.location.latitude === null || this.incident.location.longitude === null) {
    //   this.updateIncidentToAPI(false);
    //   return
    // }

    this.syncTyrePositions();

    if (this.incident.statusId < 2) {
      this.incident.statusId = 2; //Logged, in process
    }
    
    if (this.incident.statusId < 3) {
      //check if callcentre is linked, if not link them
      //This logic will need to be changed once we link fleets directly to breakdown technicians and service providers
      const callCentreLink = this.getCallCentre();
      if (!callCentreLink) {
        if (this.sharedDataService.getCallcentres.length === 1) {
          const callCentre = this.sharedDataService.getCallcentres[0];
          this.incident.incidentClients.push(new IncidentClientLink(callCentre));
          this.incident.statusId = 3; //Handed over to callcentre
          this.notification.showInfo(`Call centre ${callCentre.clientName} has been linked to the breakdown.`);
        } 
      } else {
        this.incident.statusId = 3; //Handed over to callcentre
      }
      //if there is an open whatsapp message, remove it, also udpate the message to the log
      this.sharedDataService.selectedMessages = [];
    }
    //console.log(this.incident);
    
    this.updateIncidentToAPI(false)?.subscribe();
    //now link breakdown technicians and service providers if any, link callcentre if not already linked
    //this.load5ServiceProviders();
  }

  public onClickNotifyAllViaWhatsapp() {
    this.isLoading = true;
    this.incidentService.notifyAllUsersViaWhatsapp(this.incident).subscribe({
      next: () => {
        this.isLoading = false;
        this.notification.showSuccess('Breakdown technician has been notified via WhatsApp.');
      },
      error: (error: any) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to notify breakdown technician via WhatsApp.');
      }
    });
  }

  public onClickCancelIncident() {
    this.selectedCancelReasonId = null;
    this.isCancelIncidentModalVisible = true;
  }

  public onConfirmCancelIncident() {
    if (!this.selectedCancelReasonId) {
      this.notification.showError('Please select a cancel reason');
      return;
    }
    
    console.log('Cancel Reason ID:', this.selectedCancelReasonId);
    
    this.isCancelIncidentModalVisible = false;
    this.incidentService.deleteIncident(this.incident.incidentId, this.selectedCancelReasonId).subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        this.notification.showSuccess("Breakdown has been cancelled.");
        this.router.navigate(['/incidents']);
      },
      error: (error) => {
        this.notification.handleError(error, "Failed to cancel breakdown. Please try again.");
      }
    });
  }

  public sortByDistance = (a: IncidentClientLink, b: IncidentClientLink) =>
    (a.incidentDistance ?? Number.MAX_SAFE_INTEGER) - (b.incidentDistance ?? Number.MAX_SAFE_INTEGER);
  public sortByEta = (a: IncidentClientLink, b: IncidentClientLink) =>
    (a.incidentTravelTimeMinutes ?? Number.MAX_SAFE_INTEGER) - (b.incidentTravelTimeMinutes ?? Number.MAX_SAFE_INTEGER);

  public spSearchText: string = '';

  public getUnlinkedServiceProviders(): ClientLink[] {
    const linkedIds = new Set(this.incident.incidentClients.map(c => c.clientId));
    return this.sharedDataService.getServiceProviders.filter(
      sp => !linkedIds.has(sp.clientId) &&
            sp.clientName.toLowerCase().includes(this.spSearchText.toLowerCase())
    );
  }

  public onSelectServiceProviderFromSearch(event: any): void {
    const clientId: number = event.nzValue;
    //setTimeout(() => ); //we have a settimeout because ngZorro writes the nzLabel back to the input after selectionChange fires.
    this.isLoading = true;
    this.incidentService.linkServiceProvider(this.incident.incidentId, clientId).subscribe({
      next: (updatedIncident) => {
        this.incident = this.hydrateIncident(updatedIncident);
        this.isLoading = false;
        this.spSearchText = '';
        const sp = this.sharedDataService.getServiceProviders.find(s => s.clientId === clientId);
        this.notification.showSuccess(`${sp?.clientName ?? 'Service provider'} has been linked to the breakdown.`);
        this.runServiceProviderInformation();
      },
      error: (error) => {
        this.isLoading = false;
        this.spSearchText = '';
        this.notification.handleError(error, 'Failed to link service provider.');
      }
    });
  }

  public load5ServiceProviders(){
    console.log("Loading service prodivers");
    
    this.incidentService.linkServiceProviders(this.incident.incidentId).subscribe({
      next: (updatedIncident) => {
        this.incident = this.hydrateIncident(updatedIncident);
        //once new loaded, start firing distance and stock checks
        this.runServiceProviderInformation()
        //TODO: Run service provider checks for new service providers only, not all
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to link service providers to breakdown');
      }
    });
  }

  public serviceProvidersBusyChecking: number[] = []; //array of clientIds that are being checked
  public serviceProvidersBusyQuoting: number[] = []; //array of clientIds that are being quoted
  public runServiceProviderInformation(force: boolean = false){
    const incidentId = this.incident.incidentId;
    for (let client of this.getServiceProviders().filter(sp => !this.serviceProvidersBusyChecking.includes(sp.clientId) && (force || sp.activeQuoteId === null || sp.activeQuoteId === 0))) {
      const clientId = client.clientId;
      this.serviceProvidersBusyChecking.push(clientId);
      this.quoteService.runServiceProviderChecks(incidentId, clientId, client).subscribe({
        next: (response) => {
          const index = this.incident.incidentId === incidentId && response.clientId === clientId
            ? this.incident.incidentClients.findIndex(link => link.clientId === clientId && link.connectionTypeId === 3)
            : -1;
          if (index !== -1) {
            // The response echoes the old link; only the distance fields belong to this check.
            this.incident.incidentClients[index] = {
              ...this.incident.incidentClients[index],
              incidentDistance: response.incidentDistance,
              incidentTravelTimeMinutes: response.incidentTravelTimeMinutes,
              incidentTravelTimeMinutesAdded: response.incidentTravelTimeMinutesAdded
            };
            //update incident in incident list
            this.incidentService.updateOrAddIncidentToList(this.incident);
            if (this.incident.tyrePositions && this.incident.tyrePositions.length > 0) {
              this.runServiceProviderQuote(clientId);
            }
          }
          //remove from busy checking array
          this.serviceProvidersBusyChecking = this.serviceProvidersBusyChecking.filter(id => id !== clientId);
        },
        error: (error) => {
          this.notification.handleError(error, `Failed to run service provider checks for ${client.clientName}`);
          //remove from busy checking array
          this.serviceProvidersBusyChecking = this.serviceProvidersBusyChecking.filter(id => id !== clientId);
        }
      });
    }
  }



  public isBusyLoadingServiceProvider(clientId: number): boolean {
    return this.serviceProvidersBusyChecking.includes(clientId);
  }

  public recalculateProviderEta(clientId: number): void {
    const incidentId = this.incident.incidentId;
    const client = this.incident.incidentClients.find(link => link.clientId === clientId && link.connectionTypeId === 3);
    if (!incidentId || !client || this.isBusyLoadingServiceProvider(clientId)) return;
    this.serviceProvidersBusyChecking.push(clientId);
    this.quoteService.runServiceProviderChecks(incidentId, clientId, client).subscribe({
      next: (response) => {
        const index = this.incident.incidentId === incidentId && response.clientId === clientId
          ? this.incident.incidentClients.findIndex(link => link.clientId === clientId && link.connectionTypeId === 3)
          : -1;
        if (index !== -1) {
          this.incident.incidentClients[index] = {
            ...this.incident.incidentClients[index],
            incidentDistance: response.incidentDistance,
            incidentTravelTimeMinutes: response.incidentTravelTimeMinutes,
            incidentTravelTimeMinutesAdded: response.incidentTravelTimeMinutesAdded
          };
          this.incidentService.updateOrAddIncidentToList(this.incident);
        }
        this.serviceProvidersBusyChecking = this.serviceProvidersBusyChecking.filter(id => id !== clientId);
      },
      error: (error) => {
        this.serviceProvidersBusyChecking = this.serviceProvidersBusyChecking.filter(id => id !== clientId);
        if (error.status === 422 && typeof error.error?.detail === 'string') {
          this.notification.showWarning(error.error.detail);
        } else {
          this.notification.handleError(error, 'Failed to recalculate the provider distance and ETA.');
        }
      }
    });
  }

  public runServiceProviderQuote(clientId: number) {
    if (this.serviceProvidersBusyQuoting.includes(clientId)) {
      return; // Already quoting this service provider
    }
    
    const client = this.incident.incidentClients.find(link => link.clientId === clientId && link.connectionTypeId === 3);
    if (!client) {
      this.notification.showError('Service provider not found');
      return;
    }

    const incidentId = this.incident.incidentId;
    this.serviceProvidersBusyQuoting.push(clientId);
    this.quoteService.runServiceProviderQuote(incidentId, clientId, client).subscribe({
      next: (response) => {
        const index = this.incident.incidentId === incidentId && response.clientId === clientId
          ? this.incident.incidentClients.findIndex(link => link.clientId === clientId && link.connectionTypeId === 3)
          : -1;
        if (index !== -1) {
          // Keep assignment and other current fields when the quoted link snapshot is stale.
          this.incident.incidentClients[index] = {
            ...this.incident.incidentClients[index],
            activeQuoteId: response.activeQuoteId,
            quoteSummary: response.quoteSummary
          };
          // Update incident in incident list
          this.incidentService.updateOrAddIncidentToList(this.incident);
        }
        // Remove from busy quoting array
        this.serviceProvidersBusyQuoting = this.serviceProvidersBusyQuoting.filter(id => id !== clientId);
      },
      error: (error) => {
        this.notification.handleError(error, `Failed to run quote for ${client.clientName}`);
        // Remove from busy quoting array
        this.serviceProvidersBusyQuoting = this.serviceProvidersBusyQuoting.filter(id => id !== clientId);
      }
    });
  }

  public isBusyQuotingServiceProvider(clientId: number): boolean {
    return this.serviceProvidersBusyQuoting.includes(clientId);
  }

  public runQuotesForAllServiceProviders() {
    // First check if we have any tyres
    if (!this.incident.tyrePositions || this.incident.tyrePositions.length === 0 || this.incident.tyrePositions.some(tp => tp.size === undefined) || this.incident.tyrePositions.some(tp => tp.size === '')) {
      return;
    }

    // Get all service providers that don't have a quote yet
    const serviceProvidersNeedingQuotes = this.incident.incidentClients.filter(
      link => link.connectionTypeId === 3 && (!link.activeQuoteId || link.activeQuoteId === 0) && link.isActive !== false
    );

    // Run quote for each service provider
    for (let serviceProvider of serviceProvidersNeedingQuotes) {
      this.runServiceProviderQuote(serviceProvider.clientId);
    }
  }


  
  public selectedSaveLocationId: string = '';
  public onSavedLocationModelChange(value: any) {
    //check if value is a number
    const locationId = Number(value);
    if (!isNaN(locationId)) {
      const selectedLocation = this.sharedDataService.savedLocations.find(loc => loc.savedLocationId === locationId);
      if (selectedLocation) {
        let tempLocation = new IncidentLocation();
        tempLocation.latitude = selectedLocation.latitude;
        tempLocation.longitude = selectedLocation.longitude;
        tempLocation.description = selectedLocation.geocodedDescription;
        tempLocation.descriptionShort = selectedLocation.descriptionShort;
        this.selectedSaveLocationId = selectedLocation.description;
        this.incident.location = tempLocation; //to update reference of location object, so that map-select detects change
        console.log("Updated incident location from saved location: ", this.incident.location);
        
      }
    }
  }


  public onClickSaveLocation() { 
    this.savedLocation.clientId = this.authService.user?.clientId || 0;
    this.savedLocation.latitude = this.incident.location.latitude;
    this.savedLocation.longitude = this.incident.location.longitude;
    this.savedLocation.geocodedDescription = this.incident.location.description || '';
    this.savedLocation.descriptionShort = this.incident.location.descriptionShort || '';
    if (!this.savedLocation.description || this.savedLocation.description.trim() === '') {
      this.notification.showError("Please enter a description for the saved location.");
      return;
    }
    this.isSavedLocationNameModalVisible = false;
    this.clientService.upsertSavedLocation(this.savedLocation).subscribe({
      next: (savedLocation) => {
        this.notification.showSuccess("Location saved successfully.");
        this.sharedDataService.savedLocations.push(savedLocation);
        //reset saved location
        this.savedLocation = new SavedLocation();
      },
      error: (error) => {
        this.notification.handleError(error, "Failed to save location. Please try again.");
      }
    });
  }

  public onClickNotifyDriver(templateId: number) {
    if (this.isSavingOrAssigning) return;
    if (!this.incident.driver || !this.incident.driver.phoneNumber || this.incident.driver.phoneNumber.trim() === '') {
      this.notification.showWarning("Please select a driver with a valid phone number before sending notification.");
      return;
    }

    this.isLoading = true;
    //If an invalid tyre location is given with an undefined size, remove it from the array
    this.incident.tyrePositions = this.incident.tyrePositions.filter(pos => pos.size !== undefined);

    // Save the incident first before sending notification
    const updateObservable = this.updateIncidentToAPI(false);
    
    if (!updateObservable) {
      this.isLoading = false;
      this.notification.showWarning("Please provide minimum breakdown details (location or driver phone number) before sending notification.");
      return;
    }

    updateObservable.subscribe({
      next: (updatedIncident: Incident) => {
        // After successful save, send WhatsApp notification
        this.incidentService.sendWhatsappTemplate(templateId, this.incident).subscribe({
          next: () => {
            this.isLoading = false;
            this.notification.showSuccess("Driver notified via WhatsApp.");
          },
          error: (error: any) => {
            this.isLoading = false;
            this.notification.handleError(error, "Failed to notify driver via WhatsApp.");
          }
        });
      },
      error: (error: any) => {
        this.isLoading = false;
        this.notification.handleError(error, "Failed to save breakdown before sending notification.");
      }
    });
  }

  public onClickAskDriverLocation(): void {
    const phone = this.incident.driver?.phoneNumber ?? '';
    let digits = phone.replace(/\D/g, '');
    if (digits.startsWith('0')) {
      digits = '27' + digits.slice(1);
    } else if (!digits.startsWith('27')) {
      digits = '27' + digits;
    }
    const url = `https://wa.me/${digits}`;
    const message = 'We are here to help with your breakdown. Please send your location.';
    navigator.clipboard.writeText(message).then(() => {
      this.notification.showInfo('Copied! Opening WhatsApp...');
      window.open(url, 'wa_driver_' + this.incident.incidentId);
    }).catch(() => {
      this.notification.showError('Could not copy to clipboard.');
      window.open(url, 'wa_driver_' + this.incident.incidentId);
    });
  }

  public getBreakdownTechnicianQuoteId(clientId: number): number {
    const bdtech = this.incident.incidentClients.find(link => link.clientId === clientId);
    return bdtech ? bdtech.activeQuoteId ? bdtech.activeQuoteId : 0 : 0;
  }

  public onClickViewQuote(quoteId: number) {
    this.router.navigate(['/quote', quoteId]);
  }

  isLoadingProofOfWork: boolean = false;
  public onClickSendPOWViaWhatsapp() {
    this.isLoading = true;
    this.incidentService.sendPOW(this.incident.incidentId).subscribe({
      next: () => {
        this.notification.showSuccess('Proof of Work sent via WhatsApp successfully.');
        this.isLoading = false;
      },
      error: () => {
        this.notification.showError('Failed to send Proof of Work via WhatsApp.');
        this.isLoading = false;
      }
    });
  }

  public onClickProofOfWork(incidentId: number) {
    this.isLoadingProofOfWork = true;
    this.reportService.generateProofOfWorkReport(incidentId).subscribe({
      next: (report: Base64Report) => {
        this.notification.showSuccess('Proof of Work report generated successfully.');
        this.downloadFile(report);
        this.isLoadingProofOfWork = false;
      },
      error: () => {
        this.notification.showError('Failed to generate Proof of Work report.');
        this.isLoadingProofOfWork = false;
      }
    });
  }


  private downloadFile(report: Base64Report) {
    const link = document.createElement('a');
    link.href = 'data:' + report.fileType + ';base64,' + report.base64Data;
    link.download = report.fileName;
    link.click();
  }

  public onClickNotifyServiceProvider(link: IncidentClientLink) {
    
    //Run call to notify service provider of the incident
    this.incidentService.notifyServiceProviderOfIncident(this.incident.incidentId, link.clientId, this.incident).subscribe({
      next: (incident) => {
        this.notification.showSuccess(`Service provider ${link.clientName} has been notified of the breakdown.`);
        this.incident = this.hydrateIncident(incident);
      },
      error: (error) => {
        this.notification.handleError(error, "Failed to notify service provider.");
      }
    });
  }

  public onClickAssignWorkReport(documentId: number) {
    this.incidentService.assignWorkReportToIncident(this.incident.incidentId, documentId).subscribe({
      next: (incident) => {
        this.notification.showSuccess(`Work report has been assigned to the breakdown.`);
        this.incident = this.hydrateIncident(incident);
        this.isIncidentCompleteModalVisible = true;
        //this.currentStep = 3; //Change to last tab :)
      },
      error: (error) => {
        this.notification.handleError(error, "Failed to assign work report.");
      }
    });
  }

  public onClickCompleteWithoutWorkReport() {
    this.isCompleteWithoutWorkReportConfirmModalVisible = false;
    this.incidentService.completeWithoutWorkReport(this.incident.incidentId)?.subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        this.isIncidentCompleteModalVisible = true;
      },
      error: (error) => {
        this.notification.handleError(error, "Failed to complete breakdown without work report.");
      }
    });
  }

  public onClickNavigateHome() {
    this.isIncidentCompleteModalVisible = false;
    this.router.navigate(['/home'])
  }


  public onClickGoToServiceProviderQuote() {
    const quoteId = this.incident.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true)?.activeQuoteId;
    if (quoteId && quoteId > 0)
      this.router.navigate(['/quote', quoteId]);
    else
      this.notification.showWarning("No active quote found for the selected service provider.");
  }

  public getAssignedServiceProviderQuoteId(): number {
    const spLink = this.incident.incidentClients.find(link => link.connectionTypeId === 3 && link.isActive === true);
    return spLink ? spLink.activeQuoteId ? spLink.activeQuoteId : 0 : 0;
  }

  public onClickSelectServiceProvider(link: IncidentClientLink) {
    if (this.isAssigningServiceProvider) return;
    const clientId = link.clientId;

    // Reuse an existing save, but keep the whole save -> assign operation busy.
    const saveRequest = this.incidentSaveRequest ?? this.updateIncidentToAPI();
    if (!saveRequest) {
      this.notification.showWarning('Please provide a location or driver phone number before assigning a provider.');
      return;
    }
    this.isAssigningServiceProvider = true;
    this.isLoading = true;
    let assigning = false;
    let assignmentSucceeded = false;
    saveRequest.pipe(
      concatMap(updatedIncident => {
        // checkDefaultValues may add an empty tyre row; exclude it from the assignment payload.
        const copiedIncident = JSON.parse(JSON.stringify(updatedIncident)) as Incident;
        copiedIncident.tyrePositions = copiedIncident.tyrePositions.filter(pos => pos.size !== undefined);
        assigning = true;
        return this.incidentService.assignServiceProviderToIncident(updatedIncident.incidentId, clientId, copiedIncident);
      }),
      finalize(() => {
        this.isAssigningServiceProvider = false;
        this.isLoading = false;
        if (assignmentSucceeded && !this.incident.clientId) this.checkDefaultValues();
      })
    ).subscribe({
      next: (incident) => {
        this.notification.showSuccess(`Service provider ${link.clientName} has been assigned to the breakdown. A WhatsApp notification has been sent to you if you want to forward the details.`, 2000);
        this.incident = this.hydrateIncident(incident);
        this.getServiceProviderBreakdownTechnicians();
        assignmentSucceeded = true;
      },
      error: (error) => {
        this.notification.handleError(error, assigning
          ? 'Failed to assign service provider.'
          : 'Failed to update breakdown before assigning service provider.');
      }
    });
  }

  public onClickAssignCallcentre(clientId: number) {
    const spLink = this.sharedDataService.serviceProviderLinks.find(sp => sp.clientId === clientId);
    const callcentreId = spLink?.callcentreId;
    if (!callcentreId) {
      this.notification.showError('Could not determine the callcentre for this service provider.');
      return;
    }
    this.isLoading = true;
    this.incidentService.assignCallcentre(this.incident.incidentId, callcentreId).subscribe({
      next: (incident) => {
        this.isLoading = false;
        this.incident = this.hydrateIncident(incident);
        this.notification.showSuccess('Callcentre has been assigned to the breakdown.', 2000);
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to assign callcentre.');
      }
    });
  }

  public isSelectedServiceProvider(link: IncidentClientLink): boolean {
    return link.isActive === true;
  }

  public onAddNewDriver() {
    // Keep existing values if they exist (pre-filled data)
    if (!this.incident.driver) {
      this.incident.driver = new Driver();
    }
    this.isAddDriverModalVisible = true;
  }

  public onAddNewVehicle() {
    // Keep existing values if they exist (pre-filled data)
    if (!this.incident.fleetVehicle) {
      this.incident.fleetVehicle = new Vehicle();
    }
    this.isAddVehicleModalVisible = true;
  }

  public onAddNewFleet() {
    this.fleetClient = new FleetClient();
    this.fleetClient.clientName = this.fleetSearchText;
    this.isAddFleetModalVisible = true;
  }

  public onFleetSearchChange(value: string) {
    if (value) {
      this.fleetSearchText = value;
    }
  }

  public onFleetGroupNameResolved(name: string): void {
    this.fleetClient.whatsappGroupName = name;
  }

  public onSelectFleetPricing(pricing: PriceTemplate) {
    this.fleetClient.pricingId = pricing.pricingTemplateId;
    this.selectedPricing = pricing;
  }

  public onConfirmAddDriver() {
    if (!this.incident.driver.name || this.incident.driver.name.trim() === '') {
      this.notification.showError('Please enter a driver name');
      return;
    }
    if (!this.incident.driver.phoneNumber || this.incident.driver.phoneNumber.trim() === '') {
      this.notification.showError('Please enter a driver phone number');
      return;
    }

    this.isLoading = true;
    this.clientService.addFleetTruckDriver(this.incident.clientId, this.incident.driver).subscribe({
      next: (driver) => {
        this.isLoading = false;
        this.isAddDriverModalVisible = false;
        this.notification.showSuccess('Driver added successfully');
        // Add the new driver to the local list
        this.truckDrivers.push(driver);
        // Update the driver with the returned data (includes driverId)
        this.incident.driver.driverId = driver.driverId;
        this.incident.driver.name = driver.name;
        this.incident.driver.phoneNumber = driver.phoneNumber;
        // Save incident
        if (!this.incident.incidentId || this.incident.incidentId === 0) {
          this.updateIncidentToAPI()?.subscribe();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to add driver');
      }
    });
  }

  public onConfirmAddVehicle() {
    if (!this.incident.fleetVehicle.registrationNumber || this.incident.fleetVehicle.registrationNumber.trim() === '') {
      this.notification.showError('Please enter a vehicle registration number');
      return;
    }

    this.isLoading = true;
    this.clientService.addFleetVehicle(this.incident.clientId, this.incident.fleetVehicle).subscribe({
      next: (vehicle) => {
        this.isLoading = false;
        this.isAddVehicleModalVisible = false;
        this.notification.showSuccess('Vehicle added successfully');
        // Add the new vehicle to the local list
        this.fleetVehicles.push(vehicle);
        // Update the vehicle with the returned data (includes vehicleId)
        this.incident.fleetVehicle.vehicleId = vehicle.vehicleId;
        this.incident.fleetVehicle.registrationNumber = vehicle.registrationNumber;
        // Save incident
        if (!this.incident.incidentId || this.incident.incidentId === 0) {
          this.updateIncidentToAPI()?.subscribe();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to add vehicle');
      }
    });
  }

  public onConfirmAddFleet() {
    if (!this.fleetClient.clientName || this.fleetClient.clientName.trim() === '') {
      this.notification.showError('Please enter a fleet controller name');
      return;
    }
    this.fleetClient.connectionTypeId = 1; //Fleet
    this.fleetClient.clientId = 0; //New fleet
    this.fleetClient.pricingId = this.sharedDataService.priceTemplates[0].pricingTemplateId; //Default pricing, user can change later
    this.clientService.upsertFleet(this.authService.user!.clientId, this.fleetClient).subscribe({  
      next: (fleet) => {
        this.notification.showSuccess('Fleet Controller added successfully');
        fleet.connectionTypeId = 1; //forcing the connection to the incident to be fleet type (1), the connection type being sent from upsertFleet is clientConnection type (fleet to call centre which is 2)
        this.sharedDataService.addFleetClientLink(fleet);
        this.onSelectFleet(fleet);
        this.isAddFleetModalVisible = false;
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to add fleet controller');
      }
    });
  }

  public onClickGenerateQuoteToFleet(quoteId: number) {
    this.quoteService.generateQuoteToFleet(quoteId, this.incident.incidentId, this.incident.clientId, this.authService.user!.clientId).subscribe({
      next: (response) => {
        this.notification.showSuccess('Quote generated for fleet controller');
        this.router.navigate(['/quote', response.quoteId]);
      },
      error: (error) => {
        this.notification.handleError(error, "Failed to generate quote for fleet controller.");
      }
    });
  }

  public onClickCloseIncident() {
    this.isCloseIncidentModalVisible = true;
  }

  public onIncidentClosed() {
    // Refresh the incident or navigate away
    this.router.navigate(['/incidents']);
  }

  public viewInvoice(quoteId: number) {
    this.router.navigate(['/invoice', quoteId]);
  }

  public handleUploadOk() { 
    this.isUploadModalVisible = false;
  }

  public onUploadComplete(event: any): void {
    // Handle upload completion
    this.isUploadModalVisible = false;
  }

  public selectDelayMinutes(minutes: number) {
    this.delayMinutes = minutes;  
  }

  public getUpdatedETA(): Date {
    const eta = this.incident.breakdownTechnicianDriverEta ? new Date(this.incident.breakdownTechnicianDriverEta) : new Date();
    eta.setMinutes(eta.getMinutes() + this.delayMinutes);
    return eta;
  }

  public mustDisplayCallcentreAccept(): boolean {
    if (!this.authService.user?.clientId) return false;
    const ccLink = this.incident.incidentClients.find(
      link => link.connectionTypeId === 2 && link.clientId === this.authService.user?.clientId
    );
    return ccLink ? ccLink.isActive === null || ccLink.isActive === undefined : false;
  }

  public mustDisplayServiceProviderAcceptReject() : boolean {
    //Check the incident clients and if the hasAccepted is false or null for this user's service provider, return true
    const spLink = this.incident.incidentClients.find(link => link.connectionTypeId === 3 && link.clientId === this.authService.user?.clientId);
    if (!spLink) {
      return false;
    }
    if (spLink.hasSpAccepted === false || spLink.hasSpAccepted === null || spLink.hasSpAccepted === undefined) {
      return true
    }
    return false;
  }

  public confirmLogDelay() {
    if (this.delayMinutes <= 0) {
      this.notification.showError("Please select delay minutes.");
      return;
    }
    this.isLoading = true;
    this.incidentService.addDelayToIncident(this.incident, this.delayMinutes, this.delayReason).subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        this.isLoading = false;
        this.isDelayDrawerVisible = false;
        this.notification.showSuccess("Delay has been logged to the breakdown.");
        this.delayMinutes = 0;
        this.delayReason = '';
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, "Failed to log delay to breakdown. Please try again.");
      }
    });
  }

  public onServiceProviderAccept(delayMinutes: number) {
    this.isLoading = true;
    this.incidentService.serviceProviderAcceptIncident(this.incident, delayMinutes).subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        const delayText = delayMinutes > 0 ? ` with ${delayMinutes} minutes delay` : ' with no delay';
        this.notification.showSuccess(`Breakdown accepted${delayText}`);
        this.isLoading = false;
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to accept breakdown');
        this.isLoading = false;
      }
    });
  }

  public onServiceProviderReject() {
    this.isLoading = true;
    this.incidentService.serviceProviderRejectIncident(this.incident.incidentId).subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        this.notification.showSuccess('Breakdown rejected successfully');
        this.isLoading = false;
        // Navigate away after rejection
        this.router.navigate(['/home']);
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to reject breakdown');
        this.isLoading = false;
      }
    });
  }

  public onCallcentreAccept() {
    this.isLoading = true;
    this.incidentService.callcentreAcceptIncident(this.incident.incidentId, this.authService.user!.clientId).subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        this.notification.showSuccess('Breakdown accepted successfully');
        this.isLoading = false;
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to accept breakdown');
        this.isLoading = false;
      }
    });
  }

  public onCallcentreReject() {
    this.isLoading = true;
    this.incidentService.callcentreRejectIncident(this.incident.incidentId, this.authService.user!.clientId).subscribe({
      next: () => {
        this.notification.showSuccess('Breakdown declined successfully');
        this.isLoading = false;
        this.router.navigate(['/home']);
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to decline breakdown');
        this.isLoading = false;
      }
    });
  }

  public onClickTechStatusUpdate(clientId: number) {
    return this.spService.requestTechnicianStatusUpdate(clientId).subscribe({
      next: (status) => {
        this.notification.showSuccess(`Status update requested.`);
      },
      error: (error) => {
        this.notification.handleError('Error requesting status update: ', error.message);
      }
    });
  }

  onDocumentRemoved(incident: Incident) {
    this.incident = this.hydrateIncident(incident);
  }

  public getTechStatusText(statusId: number): string {
    switch(statusId) {
      case 1: return 'Available';
      case 2: return 'Unavailable';
      case 3: return 'Busy';
      default: return 'Unknown';
    }
  }

  public getTechStatusColor(statusId: number): string {
    switch(statusId) {
      case 1: return '#52c41a'; 
      case 2: return '#ff4d4f'; 
      case 3: return '#faad14'; 
      default: return '#d9d9d9'; 
    }
  }

  public isSPMapModalVisible = false;
  public serviceProviderClients = signal<ServiceProviderClient[]>([]);

  public onClickViewSPMap() {
    this.getServiceProviderClients();
    this.isSPMapModalVisible = true;
  }

  public getServiceProviderClients() {
    const clientLinks = this.getServiceProviders();
    this.serviceProviderClients.set(clientLinks.map(m => {
      const sp = new ServiceProviderClient();
      sp.clientId = m.clientId;
      sp.clientName = m.clientName;
      sp.latitude = m.latitude;
      sp.longitude = m.longitude
      sp.clientTel = m.clientTel;
      sp.clientCell = m.clientCell;
      const technician = new BreakdownTechnician();
      technician.statusId = m.technicianStatusId || 0;
      sp.technicians = [technician];
      return sp;
    }));
  }

get incidentActionSteps(): Array<{ title: string; description: string; dotColor: string; icon: string }> {
    const actions = this.incident?.incidentActions;
    const incident = this.incident;
    if (!incident) return [];

    const fmt = (date: Date | null | undefined): string =>
      date ? new Date(String(date) + 'Z').toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

    const delayReasonsSuffix = incident.incidentDelays?.length
      ? '\n' + incident.incidentDelays.map(d => `+${d.delayMinutes}min: ${d.delayReason}`).join('\n')
      : '';

    const stepDefs: Array<{ title: string; date: Date | null | undefined; icon: string; color: string; extraDesc?: string }> = [
      { title: 'Logged',               date: incident.dtLogged,                    icon: 'file-add',     color: incident.dtLogged ? '#18a100' : '#bfbfbf' },
      { title: 'Provider Allocated',   date: actions?.dtSPAssigned,                icon: 'check-circle', color: actions?.dtSPAssigned ? '#18a100' : actions?.dtSPAsked ? '#c46f1a' : '#bfbfbf' },
      { title: 'Technician Accepted',  date: actions?.dtTechAssigned,              icon: 'user',         color: actions?.dtTechAssigned ? '#18a100' : actions?.dtTechAsked ? '#c46f1a' : '#bfbfbf' },
      { title: 'ETA Confirmed',        date: actions?.dtTechConfirmedEta,          icon: 'clock-circle', color: actions?.dtTechConfirmedEta ? '#18a100' : '#bfbfbf' },
      { title: 'ETA',                  date: incident.breakdownTechnicianDriverEta, icon: 'car',         color: incident.breakdownTechnicianDriverEta ? '#18a100' : '#bfbfbf' },
      ...(actions?.dtTechDelayAdded ? [{ title: 'Delay Added', date: actions.dtTechDelayAdded, icon: 'clock-circle', color: '#c46f1a', extraDesc: delayReasonsSuffix }] : []),
      { title: 'Technician Arrived',   date: actions?.dtTechArrived,               icon: 'environment',  color: actions?.dtTechArrived ? '#18a100' : '#bfbfbf' },
      { title: 'Completed',            date: actions?.dtCompleted,                 icon: 'check-square', color: actions?.dtCompleted ? '#18a100' : '#bfbfbf' },
    ];

    return stepDefs.map(step => ({
      title: step.title,
      description: fmt(step.date) + (step.extraDesc ?? ''),
      icon: step.icon,
      dotColor: step.color,
    }));
  }

  public getIncidentActionCurrentStep(): number {
    const steps = this.incidentActionSteps;
    const firstIncomplete = steps.findIndex(s => !s.description);
    return firstIncomplete === -1 ? steps.length : firstIncomplete;
  }

  public onClickUnassignProvider() {
    this.isLoading = true;
    this.incidentService.unassignSPFromIncident(this.incident.incidentId).subscribe({
      next: (incident) => {
        this.incident = this.hydrateIncident(incident);
        this.notification.showSuccess('Service provider has been unassigned from the breakdown.');
        this.isLoading = false;
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to unassign service provider from the breakdown.');
        this.isLoading = false;
      }
    });
  }

  public onClickDecodeLocationLink(){
    if (!this.locationLink || this.locationLink.trim() === '') {
      this.notification.showError('Please enter a location link to decode.');
      return;
    }
    this.isLoading = true;

    this.getLatLongFromUrl(this.locationLink).then(([lat, lng]) => {
      if (lat !== null && lng !== null) {
        let tempLocation = new IncidentLocation();
        tempLocation.latitude = lat;
        tempLocation.longitude = lng;
        this.incident.location = tempLocation;
        this.geocodeLocation(new google.maps.LatLng(lat, lng));
        this.notification.showSuccess('Location link decoded successfully and updated on the breakdown.');
      } else {
        this.notification.showError('Failed to decode location link. Please ensure it is a valid Google Maps link.');
      }
      this.isLoading = false;
    });
  }

  public extractDetails(){
    this.incidentService.decodeIncidentMessage(this.incident.incidentDescription).subscribe({
      next: (response : Incident) => {
        if (response) {
         try {
            const decoded = response;

            if (decoded.location) {
              let tempLocation = new IncidentLocation();
              tempLocation.latitude = decoded.location.latitude;
              tempLocation.longitude = decoded.location.longitude;
              tempLocation.description = decoded.location.description;
              tempLocation.descriptionShort = decoded.location.descriptionShort;
              this.incident.location = tempLocation;
            }

            if (decoded.fleetVehicle) {
              if (!this.incident.fleetVehicle) {
                this.incident.fleetVehicle = new Vehicle();
              }
              this.incident.fleetVehicle.registrationNumber = decoded.fleetVehicle.registrationNumber;
              const matchedVehicle = this.fleetVehicles.find(
                v => v.registrationNumber === decoded.fleetVehicle.registrationNumber
              );
              this.incident.fleetVehicle.vehicleId = matchedVehicle?.vehicleId ?? 0;
            }

            if (decoded.driver) {
              if (!this.incident.driver) {
                this.incident.driver = new Driver();
              }
              this.incident.driver.name = decoded.driver.name;
              this.incident.driver.phoneNumber = decoded.driver.phoneNumber;
              const matchedDriver = this.truckDrivers.find(
                d => d.phoneNumber === decoded.driver.phoneNumber
              );
              this.incident.driver.driverId = matchedDriver?.driverId ?? 0;
            }

            // Fleet controller
            if (decoded.clientId && decoded.clientId > 0) {
              const matchedFleet = this.sharedDataService.getFleets.find(f => f.clientId === decoded.clientId);
              if (matchedFleet) {
                this.onSelectFleet(matchedFleet);
              }
            } else if (decoded.clientName) {
              const matchedFleet = this.sharedDataService.getFleets.find(
                f => f.clientName.toLowerCase() === decoded.clientName.toLowerCase()
              );
              if (matchedFleet) {
                this.onSelectFleet(matchedFleet);
              }
            }

            // Transporter
            if (decoded.transporterName && decoded.transporterName.trim() !== '') {
              this.incident.transporterName = decoded.transporterName.trim().toUpperCase();
            }

            // Tyre positions (size and qty)
            if (decoded.tyrePositions && decoded.tyrePositions.length > 0) {
              const hasOnlyDefaultTyre = this.incident.tyrePositions.length === 1 &&
                this.incident.tyrePositions[0].tyreLocation === TyreLocation.trailer &&
                this.incident.tyrePositions[0].tyreNumber === 0 &&
                !this.incident.tyrePositions[0].size;
              if (this.incident.tyrePositions.length === 0 || hasOnlyDefaultTyre) {
                this.incident.tyrePositions = decoded.tyrePositions;
              } else {
                // Update size/qty on existing positions from the first decoded tyre
                const ref = decoded.tyrePositions[0];
                if (ref.size) {
                  for (const tyre of this.incident.tyrePositions) {
                    tyre.size = ref.size;
                    if (ref.qty) tyre.qty = ref.qty;
                  }
                }
              }
            }

            // Incident type (tyre/engine/both)
            if (decoded.incidentType !== undefined && decoded.incidentType !== null) {
              this.incident.incidentType = decoded.incidentType;
            }

            // Tyre brands
            if (decoded.tyreBrands && decoded.tyreBrands.length > 0) {
              const hasOnlyDefaultBrand = this.incident.tyreBrands.length === 1 && this.incident.tyreBrands[0] === -1;
              if (hasOnlyDefaultBrand) {
                this.incident.tyreBrands = decoded.tyreBrands;
              }
            }

            if (decoded.incidentDescription && decoded.incidentDescription.trim() !== '' && decoded.incidentDescription.trim() !== this.incident.incidentDescription.trim()) {
              this.incident.incidentDescription = this.incident.incidentDescription + '\n' + decoded.incidentDescription;
            }

            this.notification.showSuccess('Details extracted and updated on the breakdown.');
          } catch (e) {
            this.notification.showError('Failed to parse extracted details. Please ensure the description is in the correct format.');
          }
        } else {
          this.notification.showError('Failed to extract details from the description. Please ensure it is in the correct format.');
        }
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to extract details from the description.');
      }
    });
  }
}
